import React, { useEffect, useState } from 'react';
import { Descriptions, Input, InputNumber, Modal, message } from 'antd';
import { ConsignmentItem } from '../../model/consignment';

export interface ReturnToOwnerPayload {
    consignment_item_id: number;
    return_quantity: number;
    note?: string;
}

interface Props {
    open: boolean;
    item: ConsignmentItem | null;
    onClose: () => void;
    onReturn: (payload: ReturnToOwnerPayload) => Promise<void>;
}

export const ConsignmentReturnModal: React.FC<Props> = ({ open, item, onClose, onReturn }) => {
    // Consignment quantities are whole pieces only. The field starts EMPTY
    // (undefined) -- "0" shown below is a visual placeholder only, never a
    // real submitted default.
    const [quantity, setQuantity] = useState<number | undefined>(undefined);
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(false);
    const remaining = Number(item?.latest_actual_remaining || 0);

    useEffect(() => {
        if (open) {
            setQuantity(undefined);
            setNote('');
        }
    }, [open, item?.id, remaining]);

    if (!item) return null;

    const handleSubmit = async () => {
        if (!item.id) {
            message.error('ไม่พบรายการสินค้าที่ต้องการคืน');
            return;
        }
        if (quantity === undefined || quantity === null) {
            message.error('กรุณาระบุจำนวนที่คืน');
            return;
        }
        if (!Number.isInteger(quantity)) {
            message.error('จำนวนที่คืนต้องเป็นจำนวนเต็ม (ไม่รองรับทศนิยม) เนื่องจากสินค้าฝากขายนับเป็นชิ้น');
            return;
        }
        if (quantity <= 0 || quantity > remaining) {
            message.error(`จำนวนคืนต้องมากกว่า 0 และไม่เกินจำนวนคงเหลือ ${remaining}`);
            return;
        }
        try {
            setLoading(true);
            await onReturn({
                consignment_item_id: item.id,
                return_quantity: quantity,
                note: note.trim() || undefined
            });
            onClose();
        } catch (error: any) {
            message.error(error?.message || 'บันทึกการคืนสินค้าให้เจ้าของไม่สำเร็จ');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            className="consignment-return-modal"
            open={open}
            title="↩️ คืนสินค้าให้ผู้ฝาก"
            onCancel={onClose}
            onOk={handleSubmit}
            confirmLoading={loading}
            okText="ยืนยันคืนผู้ฝาก"
            cancelText="ยกเลิก"
            okButtonProps={{ danger: true, disabled: remaining <= 0 }}
            destroyOnClose
        >
            <Descriptions
                className="consignment-return-summary"
                size="small"
                column={1}
                bordered
                style={{ marginBottom: 16 }}
            >
                <Descriptions.Item label="สินค้า">{item.product_name || '-'}</Descriptions.Item>
                <Descriptions.Item label="Batch">#{item.batch_no || 1}</Descriptions.Item>
                <Descriptions.Item label="คงเหลือก่อนคืน">{remaining} {item.product_unit_name || ''}</Descriptions.Item>
            </Descriptions>

            <label className="consignment-return-field-label" style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
                จำนวนที่คืน
            </label>
            <InputNumber
                min={1}
                max={remaining}
                precision={0}
                placeholder="0"
                value={quantity}
                onChange={value => setQuantity(value === null ? undefined : Number(value))}
                style={{ width: '100%', marginBottom: 12 }}
                data-testid="input-return-quantity"
            />
            <div className="consignment-return-helper" style={{ marginBottom: 14, fontSize: 13 }}>
                คงเหลือหลังคืน: <strong>{Math.max(0, remaining - (quantity ?? 0))}</strong> {item.product_unit_name || ''}
                <br />การคืนสินค้าไม่เพิ่มยอดขายและไม่สร้างยอดจ่าย
            </div>

            <label className="consignment-return-field-label" style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
                หมายเหตุ
            </label>
            <Input
                value={note}
                onChange={event => setNote(event.target.value)}
                placeholder="เช่น เจ้าของขอรับสินค้าคงเหลือคืน"
                data-testid="input-return-note"
            />
        </Modal>
    );
};

export default ConsignmentReturnModal;
